import {IStateStore}       from '../../ap-interface';
import {Injectable}        from '@angular/core';
import {Store}             from '../index';
import {ApSignalrService}  from '../../ap-core/services/ap-signalr.service';
import {ApDateService}     from '../../ap-core/services/ap-date-service';
import {debounceTime}      from 'rxjs/operators';
import {APP_CONFIGURATION} from '../../ap-core/config';
import ISoilSamplePlanning = Data.IpsPlanning.ISoilSamplePlanning;
import ISoilSamplePlanningExportData = Data.IpsPlanning.ISoilSamplePlanningExportData;
import {
  ApCustomTypes, ExportShapeSoilSamplePlanning,
  ExportShapeSoilSamplePlanningFail,
  ExportShapeSoilSamplePlanningSuccess, LoadSoilSamplePlanning,
  LoadSoilSamplePlanningSuccess,
  LoadSoilSampleScanning, LoadSoilSampleScanningSuccess,
  NetTypes, SaveSoilSamplePlanning, SaveSoilSamplePlanningSaveSuccess
} from '@backend';

export type SoilSampleScanning = {
  Id: System.IGuid;
  MinScanDate: Date;
  MaxScanDate: Date;
  ScanCov: number;
};

interface ISoilSamplePlanningStore extends IStateStore<ISoilSamplePlanning> {
  shapeExport: ArrayBuffer;
  scanningData: SoilSampleScanning[];
}

@Injectable({providedIn: 'root'})
export class SoilSamplePlanningStore extends Store<ISoilSamplePlanningStore> {
  constructor(public backend: ApSignalrService,
              private dateService: ApDateService) {
    super(backend, {
      data: [],
      scanningData: [],
      loaded: false,
      loading: false,
      shapeExport: null
    });

    backend.registerObservable(LoadSoilSamplePlanningSuccess).subscribe(d => {
      super.Mutate(s => s.data, () => d.Data);
      super.SetLoadFinishState();
    });

    backend.registerObservable(ExportShapeSoilSamplePlanningSuccess).subscribe(d => {
      super.Mutate(s => s.shapeExport, () => d.Data);
      super.SetLoadFinishState();
    });

    backend.registerObservable(ExportShapeSoilSamplePlanningFail).subscribe(d => {
      super.Mutate(s => s.shapeExport, () => null);
      super.SetLoadFinishState();
    });

    backend.registerObservable(SaveSoilSamplePlanningSaveSuccess).subscribe(d => {
      if (d.Error) {
        console.warn(`error on save ${d.Error}`);
        return;
      }
      if (!Array.isArray(d.Data)) {
        console.warn(`expect array but was `, d.Data);
        return;
      }
      super.Mutate(s => s.data, o => {
        for (const plan of d.Data) {
          o.Replace(p => p.Id === plan.Id, plan, true);
        }
        return o;
      });
    });

    backend.registerObservable(LoadSoilSampleScanningSuccess).subscribe((d) => {
      super.Mutate(s => s.scanningData, () => this.setData(d));
      this.SetLoadFinishState();
    });

    this.AfterDatabaseUpdate.pipe(debounceTime(APP_CONFIGURATION.StoreMutateDebounce)).subscribe(() => this.ReloadSource());
  }

  public loadSoilSamplePlanning(): void {
    super.SetLoadState();
    super.Mutate(s => s.shapeExport, () => null);
    super.Mutate(s => s.data, () => []);
    this.DispatchBackend(new LoadSoilSamplePlanning([]));
  }

  public loadSoilSampleScanning(): void {
    super.SetLoadState();
    super.Mutate(s => s.scanningData, () => []);
    this.DispatchBackend(new LoadSoilSampleScanning([]));
  }

  public saveSoilSamplePlannings(soilSamplePlannings: ISoilSamplePlanning[]): void {
    this.DispatchBackend(new SaveSoilSamplePlanning([
      {
        Name: 'soilSamplePlannings',
        Type: ApCustomTypes.Data_IpsPlanning_SoilSamplePlanning + '[]',
        Value: soilSamplePlannings
      }
    ]));
  }

  public exportShape(exportItems: ISoilSamplePlanningExportData[], targetDate: Date, filename: string, countryCode: string): void {
    super.SetLoadState();
    super.Mutate(s => s.shapeExport, () => null);
    this.DispatchBackend(new ExportShapeSoilSamplePlanning([
      {
        Name: 'exportItems',
        Type: ApCustomTypes.Data_IpsPlanning_SoilSamplePlanningExportData + '[]',
        Value: exportItems
      },
      {Name: 'targetDate', Type: NetTypes.DATETIME, Value: targetDate},
      {Name: 'filename', Type: NetTypes.STRING, Value: filename},
      {Name: 'countryCode', Type: NetTypes.STRING, Value: countryCode}
    ]));
  }

  private setData(requestData: any): SoilSampleScanning[] {
    const result = [];
    requestData.Data.forEach((v: any) => {
      result.push({
        Id: v[0],
        MinScanDate: !!v[1] ? this.dateService.toFarmDateFromUtcGetJsDate(v[1]) : undefined,
        MaxScanDate: !!v[2] ? this.dateService.toFarmDateFromUtcGetJsDate(v[2]) : undefined,
        ScanCov: v[3]
      });
    });
    return result;
  }
}
